import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';

import { HOME, WELCOME_DEMOGRAPHICS } from '../../navigation/routes.constants';
import {
  usePendingDemographics,
  usePostMessage,
  useQuery,
} from '../../../hooks';
import {
  getPrivateToken,
  refreshPetsAfterSubmit,
  resetPetDemographicsSubmit,
  submitPetDemographics,
} from '../../../actions';
import { PET_DEMOS_SPECIES, POST_MESSAGE_TYPES } from '../../../constants';
import {
  PET_DEMOS_KEY_PREFIX,
  PET_DEMOS_STEPS,
} from '../petDemographics.constants';
import {
  arrayToSet,
  entriesToMap,
  getCancelledCounts,
  getPendingRemovalDates,
  mapToEntries,
  readPersistedState,
  setToArray,
} from '../petDemographics.utils';

// Calculates a pet's age in whole years at a given coverage start date.
const calcAgeAtCoverageStart = ({ coverageStartDate, dob }) => {
  if (!dob || !coverageStartDate) {
    return 0;
  }

  return moment
    .utc(coverageStartDate)
    .startOf('day')
    .diff(moment.utc(dob).startOf('day'), 'years');
};

// Returns a copy of a rollover pet with its age recalculated based on
// the current term's coverage start date.
const withRecalculatedAge = ({ coverageStartDate, pet }) => {
  const dob = pet.demographicData?.dateOfBirth;

  return {
    ...pet,
    demographicData: {
      ...pet.demographicData,
      age: dob
        ? calcAgeAtCoverageStart({ coverageStartDate, dob })
        : pet.demographicData?.age,
    },
  };
};

// Shapes a pet into the row format used by pet-removal state/options.
const toRemovalRow = (pet, removalDate = null) => ({
  coverageStartDate: pet.coverageStartDate || null,
  Id: pet.employeeDependentId,
  Name: pet.demographicData?.petName || '',
  removalDate,
  Specie: pet.demographicData?.species || '',
});

const usePetDemographics = (goToSpecificStep = () => { }) => {
  const usePendingDemographicsFF = usePendingDemographics();
  const sendPostMessage = usePostMessage();
  const history = useHistory();
  const query = useQuery();
  const publicToken = query.get('publicToken');
  const storageKey = `${PET_DEMOS_KEY_PREFIX}_${publicToken || 'anonymous'}`;

  // Read persisted state once on mount
  const savedStateRef = useRef(undefined);
  if (savedStateRef.current === undefined) {
    savedStateRef.current = readPersistedState(storageKey);
  }
  const saved = savedStateRef.current;

  const [coveragePeriodSelected, setCoveragePeriodSelected] = useState(
    saved?.coveragePeriodSelected ?? null,
  );
  const [petDemographics, setPetDemographics] = useState(
    saved?.petDemographics ?? [],
  );
  const [selectedPetsForRemoval, setSelectedPetsForRemoval] = useState(
    saved?.selectedPetsForRemoval ?? [],
  );
  const [isAgreementSubmitted, setIsAgreementSubmitted] = useState(
    saved?.isAgreementSubmitted ?? false,
  );
  // From Agreements step: agree (eConsent), caslAgree (acknowledged)
  const [agreementDeclarations, setAgreementDeclarations] = useState(
    saved?.agreementDeclarations ?? null,
  );
  // Store all pets information per coverage period (by groupId)
  const [coveragePeriodPetsData, setCoveragePeriodPetsData] = useState(
    () => entriesToMap(saved?.coveragePeriodPetsData),
  );
  // Track previous groupId to detect coverage period changes
  const previousGroupIdRef = useRef(null);
  // True when current petDemographics were filled from rollover pets
  // (Previous term eligible pets)
  const [
    filledFromRolloverPets,
    setFilledFromRolloverPets,
  ] = useState(saved?.filledFromRolloverPets ?? false);
  // Selected pets from rollover modal
  const [selectedRolloverPetsByGroupId, setSelectedRolloverPetsByGroupId] =
    useState(() => entriesToMap(saved?.selectedRolloverPetsByGroupId));
  // Skip and continue from rollover modal
  const [skippedRolloverGroupIds, setSkippedRolloverGroupIds] = useState(
    () => arrayToSet(saved?.skippedRolloverGroupIds),
  );
  // Original pet per index, to undo replacement (only after user saves)
  const [replacedSlotsOriginal, setReplacedSlotsOriginal] = useState(
    () => entriesToMap(saved?.replacedSlotsOriginal),
  );
  // Pending replacement: original pet stored when user confirms, moved to
  // replacedSlotsOriginal only when user clicks Save in AddPetDetailsModal
  const [, setPendingReplacementByIndex] = useState(() => new Map());

  // Mark groupId as skipped from "Skip and Continue" in rollover modal
  const setSkippedRolloverGroupId = useCallback((groupId) => {
    setSkippedRolloverGroupIds((prev) => {
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
  }, []);

  // Save selected pets from rollover modal and previous eligible pets
  const setSelectedRolloverPetsForGroup = useCallback((groupId, pets) => {
    setSelectedRolloverPetsByGroupId((prev) => {
      const next = new Map(prev);
      if (pets && pets.length > 0) {
        next.set(groupId, pets);
      } else {
        // If there are no pets means no rollover for this period group id
        next.delete(groupId);
      }
      return next;
    });

    // Clear "skipped" in case user has skipped previously
    if (pets && pets.length > 0) {
      setSkippedRolloverGroupIds((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  }, []);

  const dispatch = useDispatch();
  const store = useSelector(({
    petDemographics: petDemosReducer,
    session,
    settings,
  }) => ({ petDemosReducer, session, settings }));
  const {
    petDemosReducer: {
      errorSubmitting,
      refreshingPets,
      submitting,
      successSubmitting,
    },
    session: { privateToken },
    session,
    settings: { featureFlags },
  } = store;

  const isWebView = useMemo(() => Boolean(publicToken), [publicToken]);

  useEffect(() => {
    if (publicToken && !privateToken && !featureFlags.loading) {
      dispatch(getPrivateToken({ publicToken }));
    }
  }, [dispatch, featureFlags.loading, publicToken, privateToken]);

  const petDemosData = useMemo(() => {
    if (!usePendingDemographicsFF) {
      return {};
    }

    const { userInfo } = session;
    if (!userInfo?.PdbaData || !Array.isArray(userInfo.PdbaData)
      || userInfo.PdbaData.length === 0) {
      return {};
    }

    // Get first PdbaData item (assuming single policy for now)
    const pdbaDataItem = userInfo.PdbaData[0] || {};

    // Extract NeedsPetDemographics object
    const needsPetDemographics = pdbaDataItem.NeedsPetDemographics || {};
    const certificateNumber = Object.keys(needsPetDemographics)[0] || '';
    const demographicsData = needsPetDemographics[certificateNumber] || {};

    // Get demographics array and flatten it for compatibility
    const demographics = demographicsData.demographics || [];
    const declarations = demographicsData.declarations || {};
    return {
      ...pdbaDataItem,
      certificateNumber,
      declarations,
      NeedsPetDemographics: demographics
        .filter((term) => term.demographicChangesRequired),
    };
  }, [session, usePendingDemographicsFF]);

  // Check if eConsent has already been submitted in backend
  const isEconsentSubmitted = useMemo(
    () => !!petDemosData?.declarations?.isEconsentSubmitted,
    [petDemosData],
  );

  // Rollover pets in petDemographics
  useEffect(() => {
    if (!coveragePeriodSelected?.pets) {
      setPetDemographics([]);
      previousGroupIdRef.current = null;
      setSelectedPetsForRemoval([]);
      setFilledFromRolloverPets(false);
      setReplacedSlotsOriginal(new Map());
      setPendingReplacementByIndex(new Map());
      return;
    }

    const groupId = coveragePeriodSelected.groupId;
    const isPeriodChanged = previousGroupIdRef.current !== groupId;

    // Only update petDemographics when coverage period actually changes
    if (isPeriodChanged) {
      previousGroupIdRef.current = groupId;
      setReplacedSlotsOriginal(new Map());
      setPendingReplacementByIndex(new Map());

      const savedPetsData = coveragePeriodPetsData.get(groupId);

      // Post-submission: load pets as-is. Pets that already had complete
      // demographic data from the backend are marked isFromSubmission so
      // they render as read-only; newly-added empty pets stay editable.
      if (isEconsentSubmitted) {
        const currentPets = coveragePeriodSelected?.pets || [];
        setPetDemographics(
          currentPets.map((pet) => {
            const demo = pet.demographicData || {};
            const wasAlreadyComplete = !!demo.petName?.trim()
              && !!demo.dateOfBirth
              && !!demo.breed?.trim()
              && !!demo.species
              && Number(demo.weight) > 0;
            return {
              ...pet,
              isFromSubmission: wasAlreadyComplete,
              isRolloverPet: false,
            };
          }),
        );
        setFilledFromRolloverPets(false);
        setSelectedPetsForRemoval(savedPetsData?.removedPets ?? []);
        return;
      }

      // Pets the user chose not to roll over from the previous term. Stays
      // empty unless we're building this period's demographics for the
      // first time (i.e. not reloading data already saved for it below).
      const nonRolledOverPets = [];

      // Coverage period has already been filled (term > 1)
      if (savedPetsData?.petDemographics) {
        const recalculated = savedPetsData.petDemographics.map((pet) => (
          pet.isRolloverPet
            ? withRecalculatedAge({
              coverageStartDate: coveragePeriodSelected.coverageStartDate,
              pet,
            })
            : pet
        ));

        setPetDemographics(recalculated);
        setFilledFromRolloverPets(false);
      } else {
        // First time user fills pet demographics
        // use selected rollover (if any) or previous eligible pets
        const currentTerm = coveragePeriodSelected.policyTerm;
        const userSkippedRollover = skippedRolloverGroupIds.has(groupId);

        // Check if user has selected any rollover pets from rollover modal
        const selectedRollover = selectedRolloverPetsByGroupId.get(groupId);
        // Full previous-term eligible pets (real demographic data). Used
        // both as the "modal never visited" fallback below and as the
        // source for backfilling non-rolled-over pets further down.
        const previousPeriodData = Array.from(coveragePeriodPetsData.values())
          .find((data) => data.policyTerm === currentTerm - 1)?.eligiblePets;
        // Skipping means rolling over nobody; otherwise use whatever was
        // selected in the modal, or fall back to "roll over everyone"
        // only when the modal was never shown for this group at all.
        let rolloverSourcePets = [];

        if (!userSkippedRollover) {
          if (selectedRollover) {
            rolloverSourcePets = selectedRollover;
          } else if (previousPeriodData) {
            rolloverSourcePets = previousPeriodData;
          }
        }

        const previousEligiblePets = currentTerm > 1
          ? rolloverSourcePets.map((pet) => ({
            ...pet,
            isRolloverPet: true,
          }))
          : [];

        const currentPets = coveragePeriodSelected?.pets || [];

        // Collect IDs ineligible in any term prior to the current one so
        // those slots don't appear as fillable in this and future terms
        const historicalIneligibleIds = new Set();

        coveragePeriodPetsData.forEach((data) => {
          if (data.policyTerm < currentTerm) {
            data.ineligiblePets.forEach((ineligPet) => {
              historicalIneligibleIds.add(ineligPet.Id);
            });
          }
        });

        const isHistoricallyIneligible = (pet) => {
          const petId = pet.employeeDependentId;
          const parentId = pet.parentEmployeeDependentId;
          const rootId = pet.rootEmployeeDependentId;

          return historicalIneligibleIds.has(petId)
            || historicalIneligibleIds.has(parentId)
            || historicalIneligibleIds.has(rootId);
        };

        const adjustedCurrentPets = currentPets.filter(
          (currentPet) => !isHistoricallyIneligible(currentPet),
        );

        let petDemosInfo = [];

        // Use the full previous period list, not just the user's selection,
        // so pets that weren't rolled over are still detected and backfilled.
        const hasAncestors = previousPeriodData?.length > 0
          && currentPets.length > 0;

        if (hasAncestors) {
          const previousIds = new Set(
            previousEligiblePets.map((prev) => prev.employeeDependentId),
          );
          const previousPetByEmployeeDependentId = new Map(
            previousPeriodData.map((prev) => [prev.employeeDependentId, prev]),
          );

          const numberOfDogsPrev = previousEligiblePets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
          ).length;

          const numberOfCatsPrev = previousEligiblePets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
          ).length;

          const numberOfDogs = adjustedCurrentPets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
          ).length;

          const numberOfCats = adjustedCurrentPets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
          ).length;

          const missingDogs = Math.max(0, numberOfDogs - numberOfDogsPrev);
          const missingCats = Math.max(0, numberOfCats - numberOfCatsPrev);

          const currentPetByParentId = new Map(
            currentPets
              .filter((pet) => pet.parentEmployeeDependentId)
              .map((pet) => [pet.parentEmployeeDependentId, pet]),
          );

          const { coverageStartDate } = coveragePeriodSelected;

          // If current period has more pets than previous period
          // add them as non-rollover pets (placeholder pets)
          const base = previousEligiblePets.map((prevPet) => {
            const currentPet = currentPetByParentId.get(
              prevPet.employeeDependentId,
            );

            return {
              ...withRecalculatedAge({ coverageStartDate, pet: prevPet }),
              coverageStartDate,
              employeeDependentId: currentPet?.employeeDependentId
                ?? prevPet.employeeDependentId,
            };
          });

          // Non-rolled-over: current-term slots whose previous-term pet was
          // eligible last term but wasn't picked to roll over. Kept as its
          // own blank current-term slot (same as any new pet) so its card
          // requires the user to fill in details on Complete Pet Details —
          // actual removal is resolved later, on the Complete Pet Removal
          // step, same as any other pet.
          const nonRolledOverIds = new Set();

          currentPets.forEach((currentPet) => {
            const parentId = currentPet.parentEmployeeDependentId;
            const prevPet = previousPetByEmployeeDependentId.get(parentId);

            if (!parentId || previousIds.has(parentId) || !prevPet) {
              return;
            }

            nonRolledOverPets.push({
              ...currentPet,
              isRolloverPet: false,
            });
            nonRolledOverIds.add(currentPet.employeeDependentId);
          });

          const isNotInPrevious = (pet) => (
            !previousIds.has(pet.employeeDependentId)
            && !previousIds.has(pet.parentEmployeeDependentId)
            && !nonRolledOverIds.has(pet.employeeDependentId)
          );

          const dogsNotInPrevious = adjustedCurrentPets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog
              && isNotInPrevious(pet),
          );
          const catsNotInPrevious = adjustedCurrentPets.filter(
            (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat
              && isNotInPrevious(pet),
          );

          const extraPets = [];

          const dogsToAdd = Math.min(missingDogs, dogsNotInPrevious.length);
          for (let i = 0; i < dogsToAdd; i += 1) {
            extraPets.push({
              ...dogsNotInPrevious[i],
              isRolloverPet: false,
            });
          }

          const catsToAdd = Math.min(missingCats, catsNotInPrevious.length);
          for (let i = 0; i < catsToAdd; i += 1) {
            extraPets.push({
              ...catsNotInPrevious[i],
              isRolloverPet: false,
            });
          }

          petDemosInfo = [...base, ...nonRolledOverPets, ...extraPets];
        }

        let finalPetDemos = currentPets.map((pet) => ({
          ...pet,
          isRolloverPet: false,
        }));

        // petDemosInfo contains rollover and placeholder pets
        if (currentTerm > 1 && petDemosInfo.length > 0) {
          finalPetDemos = petDemosInfo;
        }

        setPetDemographics(finalPetDemos);
        setFilledFromRolloverPets(
          currentTerm > 1 && petDemosInfo.length > 0,
        );

        // User "Skip and Continue" from rollover modal
        // removed from skipped for next time user enters to same period
        if (userSkippedRollover) {
          setSkippedRolloverGroupIds((prev) => {
            const next = new Set(prev);
            next.delete(groupId);
            return next;
          });
        }
      }

      // Non-rolled-over pets are no longer pre-resolved here — they're
      // regular removal candidates now, resolved on the removal step
      // (including via its single-option auto-select).
      setSelectedPetsForRemoval(savedPetsData?.removedPets ?? []);
    }
  }, [
    coveragePeriodSelected,
    coveragePeriodPetsData,
    isEconsentSubmitted,
    selectedRolloverPetsByGroupId,
    skippedRolloverGroupIds,
  ]);

  const userName = useMemo(() => {
    if (!session?.userInfo) {
      return '';
    }

    return session.userInfo.FirstName;
  }, [session]);

  // Removals the backend expects are matched against pets the user can pick
  // on the removal step. A pet that was ineligible in a prior term has no
  // slot there (it's submitted as ineligible with the removal date instead),
  // so the removals it covers are dropped to avoid a step with no pet.
  const adjustedRemovalDates = useMemo(() => {
    if (!coveragePeriodSelected?.removalDates
      || !Array.isArray(coveragePeriodSelected.removalDates)) {
      return [];
    }

    const currentTerm = coveragePeriodSelected.policyTerm;
    const historicalIneligibleIds = new Set();

    coveragePeriodPetsData.forEach((data) => {
      if (data.policyTerm < currentTerm) {
        data.ineligiblePets?.forEach((ineligPet) => {
          historicalIneligibleIds.add(ineligPet.Id);
        });
      }
    });

    const shownPetIds = new Set(
      petDemographics.map((pet) => pet.employeeDependentId),
    );
    // coveragePeriodSelected.pets already excludes ineligible pets, so the
    // term's original pets are read from the backend data instead.
    const originalPets = petDemosData?.NeedsPetDemographics?.find(
      (period) => period.groupId === coveragePeriodSelected.groupId,
    )?.pets || [];
    const priorIneligiblePets = originalPets.filter((pet) => (
      !shownPetIds.has(pet.employeeDependentId)
      && (historicalIneligibleIds.has(pet.employeeDependentId)
        || historicalIneligibleIds.has(pet.parentEmployeeDependentId)
        || historicalIneligibleIds.has(pet.rootEmployeeDependentId))
    ));

    return getPendingRemovalDates({
      priorIneligiblePets,
      removalDates: coveragePeriodSelected.removalDates,
    });
  }, [
    coveragePeriodPetsData,
    coveragePeriodSelected,
    petDemographics,
    petDemosData,
  ]);

  const petRemovals = useMemo(() => {
    if (!petDemographics || !petDemographics.length
      || !adjustedRemovalDates.length) {
      return [];
    }

    const hasDogs = adjustedRemovalDates.some((remDate) => remDate.dogs > 0);
    const hasCats = adjustedRemovalDates.some((remDate) => remDate.cats > 0);

    return petDemographics
      .filter((pet) => {
        // all pets are required
        if (hasDogs && hasCats) {
          return true;
        }

        // only dogs?
        if (hasDogs && !hasCats) {
          return pet.demographicData?.species === PET_DEMOS_SPECIES.dog;
        }

        // only cats?
        if (!hasDogs && hasCats) {
          return pet.demographicData?.species === PET_DEMOS_SPECIES.cat;
        }

        // default case, mismatched data :bug:
        return false;
      })
      .map((pet) => toRemovalRow(pet));
  }, [petDemographics, adjustedRemovalDates]);

  const maxAgeByPeriod = useMemo(() => {
    if (!coveragePeriodSelected) {
      return '';
    }

    return coveragePeriodSelected.maxAge;
  }, [coveragePeriodSelected]);

  const ineligiblePets = useMemo(() => {
    if (!petDemographics || !petDemographics.length) {
      return [];
    }

    if (!coveragePeriodSelected?.maxAge) {
      return [];
    }

    const maxAge = coveragePeriodSelected.maxAge;
    const { coverageStartDate } = coveragePeriodSelected;

    // Create a map of selected pets by Id for quick lookup of removal dates
    const selectedPetsMap = new Map(
      selectedPetsForRemoval.map((pet) => [pet.Id, pet]),
    );

    return petDemographics
      .filter((pet) => {
        // Exclude pets that were selected for removal
        if (selectedPetsMap.has(pet?.employeeDependentId)) {
          return false;
        }

        // Check if pet has a date of birth
        if (!pet.demographicData?.dateOfBirth) {
          return false;
        }

        // Skip age check for rollover pets
        if (pet.isRolloverPet) {
          return false;
        }

        // A pet is ineligible if its age in whole years already exceeds maxAge
        // at the coverage effective (start) date.
        const petAge = calcAgeAtCoverageStart({
          coverageStartDate,
          dob: pet.demographicData.dateOfBirth,
        });

        return petAge > maxAge;
      })
      .map((pet) => {
        const selectedPet = selectedPetsMap.get(pet.employeeDependentId);

        return {
          age: pet.demographicData.age,
          breed: pet.demographicData.breed,
          breedId: pet.demographicData.breedId || null,
          dateOfBirth: pet.demographicData.dateOfBirth,
          Id: pet.employeeDependentId,
          Name: pet.demographicData?.petName || '',
          parentEmployeeDependentId:
            pet.parentEmployeeDependentId || null,
          policyTerm: coveragePeriodSelected.policyTerm,
          RemovalDate: selectedPet?.removalDate || null,
          rootEmployeeDependentId:
            pet.rootEmployeeDependentId || null,
          Specie: pet.demographicData?.species || '',
          weight: pet.demographicData.weight,
        };
      });
  }, [coveragePeriodSelected, petDemographics, selectedPetsForRemoval]);

  // Pets ineligible in term N are excluded from term N+1 onwards.
  const adjustedPeriodCounts = useMemo(() => {
    if (!petDemosData?.NeedsPetDemographics) {
      return new Map();
    }

    const result = new Map();

    petDemosData.NeedsPetDemographics.forEach((period) => {
      const historicalIds = new Set();
      coveragePeriodPetsData.forEach((data) => {
        if (data.policyTerm < period.policyTerm) {
          data.ineligiblePets.forEach(
            (ineligPet) => historicalIds.add(ineligPet.Id),
          );
        }
      });

      const eligiblePeriodPets = period.pets.filter(
        (pet) => !historicalIds.has(pet.employeeDependentId)
          && !historicalIds.has(pet.parentEmployeeDependentId)
          && !historicalIds.has(pet.rootEmployeeDependentId),
      );

      const dogs = eligiblePeriodPets.filter(
        (periodPet) => (
          periodPet.demographicData?.species === PET_DEMOS_SPECIES.dog
        ),
      ).length;

      const cats = eligiblePeriodPets.filter(
        (periodPet) => (
          periodPet.demographicData?.species === PET_DEMOS_SPECIES.cat
        ),
      ).length;

      // Pets already confirmed removed for this period (via the removal
      // step) no longer count toward its total. Before that's confirmed,
      // the pending cancellation still shows as an expected pet.
      const periodData = coveragePeriodPetsData.get(period.groupId);
      const confirmedCancelledDogs = periodData?.removedPetsCounts?.dogs ?? 0;
      const confirmedCancelledCats = periodData?.removedPetsCounts?.cats ?? 0;

      const numberOfDogs = Math.max(0, dogs - confirmedCancelledDogs);
      const numberOfCats = Math.max(0, cats - confirmedCancelledCats);

      // Separate, always-on cap for the rollover modal: a pet fully
      // covered by this period's own cancellation should never be
      // selectable there, even before the removal step confirms it —
      // unlike numberOfDogs/numberOfCats above, this doesn't wait on
      // removedPetsCounts.
      const { cancelledCats, cancelledDogs } = getCancelledCounts({
        coverageStartDate: period.coverageStartDate,
        removalDates: period.removalDates,
      });

      const maxRolloverDogs = Math.max(0, dogs - cancelledDogs);
      const maxRolloverCats = Math.max(0, cats - cancelledCats);

      result.set(period.groupId, {
        maxRolloverCats,
        maxRolloverDogs,
        numberOfCats,
        numberOfDogs,
        // Based on the actual slot count, not numberOfDogs + numberOfCats —
        // a pet whose species is missing/blank would be excluded from both
        // of those, silently undercounting the total.
        numberOfPets: Math.max(
          0,
          eligiblePeriodPets.length
            - confirmedCancelledDogs
            - confirmedCancelledCats,
        ),
        pets: eligiblePeriodPets,
      });
    });

    return result;
  }, [coveragePeriodPetsData, petDemosData]);

  const petQuantityToRemove = useMemo(() => {
    if (!adjustedRemovalDates.length) {
      return {
        catCount: 0,
        dogCount: 0,
        total: 0,
      };
    }

    // Sum up all removal dates to get total counts
    const totals = adjustedRemovalDates.reduce(
      (acc, removalDate) => ({
        catCount: acc.catCount + (removalDate.cats || 0),
        dogCount: acc.dogCount + (removalDate.dogs || 0),
      }),
      { catCount: 0, dogCount: 0 },
    );

    return {
      catCount: totals.catCount,
      dogCount: totals.dogCount,
      total: totals.catCount + totals.dogCount,
    };
  }, [adjustedRemovalDates]);

  const redirectionRoute = useMemo(() => {
    // if missing pet demos, then fill up them in welcome screen
    if (usePendingDemographicsFF && petDemosData
      && Object.keys(petDemosData).length) {
      return WELCOME_DEMOGRAPHICS;
    }

    // otherwise, normal redirection to home
    return '/';
  }, [petDemosData, usePendingDemographicsFF]);

  const inWelcomeScreen = useMemo(() => window.location.pathname
    === WELCOME_DEMOGRAPHICS && usePendingDemographicsFF,
  [window.location.pathname]);

  const onCoveragePeriodSelected = useCallback((coveragePeriod) => {
    setCoveragePeriodSelected(coveragePeriod);
  }, []);

  const handleUpdatePetDemographics = useCallback((updatedDetails, index) => {
    setPetDemographics((prevPetDemographics) => prevPetDemographics
      .map((pet, idx) => {
        if (idx !== index) {
          return pet;
        }

        return {
          ...pet,
          demographicData: {
            ...pet.demographicData,
            ...updatedDetails,
          },
        };
      }));
  }, []);

  // Store original pet in pending when user confirms replacement (not yet
  // "replaced" until they save in AddPetDetailsModal)
  const handleConfirmReplacement = useCallback((petToStore, index) => {
    if (!petToStore) {
      return;
    }

    setPendingReplacementByIndex((prev) => {
      const next = new Map(prev);
      next.set(index, { ...petToStore });
      return next;
    });
  }, []);

  // On save in AddPetDetailsModal (replacement flow): move pending to
  // replacedSlotsOriginal and update pet
  const handleReplacePetSave = useCallback((updatedDetails, index) => {
    setPendingReplacementByIndex((prev) => {
      const original = prev.get(index);
      if (original) {
        setReplacedSlotsOriginal((r) => {
          const next = new Map(r);
          next.set(index, original);
          return next;
        });
      }
      const next = new Map(prev);
      next.delete(index);
      return next;
    });

    setPetDemographics((prevPetDemographics) => prevPetDemographics
      .map((pet, idx) => {
        if (idx !== index) {
          return pet;
        }

        return {
          ...pet,
          demographicData: {
            ...pet.demographicData,
            ...updatedDetails,
          },
          isReplacedPet: true,
        };
      }));
  }, []);

  // User closed AddPetDetailsModal without saving; clear pending for index
  const handleCancelReplacement = useCallback((index) => {
    setPendingReplacementByIndex((prev) => {
      const next = new Map(prev);
      next.delete(index);
      return next;
    });
  }, []);

  // Restore original pet and remove from replaced map
  const handleUndoReplace = useCallback((index) => {
    setReplacedSlotsOriginal((prev) => {
      const next = new Map(prev);
      const originalPet = next.get(index);
      next.delete(index);

      if (originalPet) {
        setPetDemographics((prevPets) => prevPets.map((pet, idx) => (
          idx === index ? originalPet : pet
        )));
      }
      return next;
    });
  }, []);

  const isReplaced = useCallback(
    (index) => replacedSlotsOriginal.has(index),
    [replacedSlotsOriginal],
  );

  const getOriginalPetName = useCallback(
    (index) => replacedSlotsOriginal.get(index)?.demographicData?.petName || '',
    [replacedSlotsOriginal],
  );

  const handleSelectedPetsForRemovalChange = useCallback((
    updatedPets,
    removalDateUpdated,
  ) => {
    setSelectedPetsForRemoval((prev) => {
      // no previous selected pets
      if (prev.length === 0) {
        // so new selection is returned directly
        return updatedPets;
      }

      // removed all pets of current date
      if (updatedPets.length === 0) {
        // return all other pets selected
        return prev.filter((prevSelected) => prevSelected
          .removalDate !== removalDateUpdated);
      }

      const removalDateExisting = prev.some((prevSelected) => prevSelected
        .removalDate === removalDateUpdated);

      // if has previous selected pet by date
      if (removalDateExisting) {
        return [
          // mix all other removal date selections
          ...prev.filter((prevSelected) => prevSelected
            .removalDate !== removalDateUpdated),
          // with the new ones
          ...updatedPets,
        ];
      }

      // no previous selected pet by date, so we only mix them up
      return [
        ...prev,
        ...updatedPets,
      ];
    });
  }, [setSelectedPetsForRemoval]);

  // Store removed and ineligible pets per coverage period
  // when user completes steps
  useEffect(() => {
    const groupId = coveragePeriodSelected?.groupId;

    if (!groupId) {
      return;
    }

    // Create a Set of pet IDs that belong to this coverage period
    const currentPeriodPetIds = new Set(
      petDemographics.map((pet) => pet.employeeDependentId),
    );

    // Filter pets to only include pets that belong to this coverage period
    const filteredRemovedPets = selectedPetsForRemoval.filter((pet) => (
      currentPeriodPetIds.has(pet.Id)
    ));

    // Filter pets to only include pets that belong to this coverage period
    const filteredIneligiblePets = ineligiblePets.filter((pet) => (
      currentPeriodPetIds.has(pet.Id)
    ));

    const removedPetsCounts = filteredRemovedPets.length > 0 ? {
      cats: filteredRemovedPets.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.cat,
      ).length,
      dogs: filteredRemovedPets.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.dog,
      ).length,
      total: filteredRemovedPets.length,
    } : { cats: 0, dogs: 0, total: 0 };

    const ineligiblePetsCounts = filteredIneligiblePets.length > 0 ? {
      cats: filteredIneligiblePets.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.cat,
      ).length,
      dogs: filteredIneligiblePets.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.dog,
      ).length,
      total: filteredIneligiblePets.length,
    } : { cats: 0, dogs: 0, total: 0 };

    // Eligible pets: not in ineligiblePets nor in removedPets
    const removedPetIds = new Set(
      filteredRemovedPets.map((pet) => pet.Id),
    );

    const ineligiblePetIds = new Set(
      filteredIneligiblePets.map((pet) => pet.Id),
    );

    const eligiblePets = petDemographics.filter(
      (pet) => !removedPetIds.has(pet.employeeDependentId)
        && !ineligiblePetIds.has(pet.employeeDependentId),
    );

    const eligiblePetsCounts = eligiblePets.length > 0 ? {
      cats: eligiblePets.filter(
        (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
      ).length,
      dogs: eligiblePets.filter(
        (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
      ).length,
      total: eligiblePets.length,
    } : { cats: 0, dogs: 0, total: 0 };

    // Look up each removed pet's own selected removal date
    const removedPetById = new Map(
      filteredRemovedPets.map((pet) => [pet.Id, pet]),
    );

    const allPets = petDemographics.map((pet) => ({
      ...pet,
      demographicData: {
        ...pet.demographicData,
        isIneligible: ineligiblePetIds.has(pet.employeeDependentId),
        isRemoved: removedPetIds.has(pet.employeeDependentId),
      },
      removalDate: removedPetById.get(pet.employeeDependentId)?.removalDate
        ?? null,
    }));

    setCoveragePeriodPetsData((prev) => {
      const newMap = new Map(prev);
      const existingData = prev.get(groupId) || {};
      const currentPolicyTerm = coveragePeriodSelected?.policyTerm;
      const employeeId = coveragePeriodSelected?.employeeId;

      newMap.set(groupId, {
        ...existingData,
        allPets,
        eligiblePets,
        eligiblePetsCounts,
        employeeId,
        ineligiblePets: filteredIneligiblePets,
        ineligiblePetsCounts,
        // Store the current petDemographics state to preserve user edits
        petDemographics,
        policyTerm: currentPolicyTerm,
        removedPets: filteredRemovedPets,
        removedPetsCounts,
      });

      // Editing coverage periods will impact future periods
      // This will mark the next periods as pending information
      prev.forEach((term, termKey) => {
        if (term?.policyTerm > currentPolicyTerm) {
          newMap.delete(termKey);
        }
      });

      return newMap;
    });
  }, [
    coveragePeriodSelected?.groupId,
    ineligiblePets,
    petDemographics,
    selectedPetsForRemoval,
  ]);

  // Agreements step should only show when completing the last coverage period
  const isLastCoveragePeriod = useMemo(() => {
    const demographicsData = petDemosData?.NeedsPetDemographics || [];

    if (!demographicsData.length || !coveragePeriodSelected?.groupId) {
      return false;
    }

    const lastPeriod = demographicsData[demographicsData.length - 1];

    return lastPeriod.groupId === coveragePeriodSelected.groupId;
  }, [coveragePeriodSelected?.groupId, petDemosData?.NeedsPetDemographics]);

  const isPostSubmissionMode = useMemo(
    () => isEconsentSubmitted && !!coveragePeriodSelected,
    [isEconsentSubmitted, coveragePeriodSelected],
  );

  // on continue step 3
  const onCompletePetInfoContinuePress = useCallback(() => {
    const hasPetRemoval = petQuantityToRemove.total > 0;
    const hasIneligiblePets = !!(coveragePeriodSelected?.maxAge
      && ineligiblePets && ineligiblePets.length);

    // Mark step 3 (COMPLETE_PET_DETAILS) as completed for this period
    const groupId = coveragePeriodSelected?.groupId;
    if (groupId) {
      setCoveragePeriodPetsData((prev) => {
        const newMap = new Map(prev);
        const existingData = prev.get(groupId) || {};
        newMap.set(groupId, {
          ...existingData,
          hasCompletedPetDetails: true,
          // Whether step 6 must be shown to the user. Not the same as
          // "were pets removed" (removedPets can be non-empty from
          // rollover-driven auto-removals even when there's nothing left
          // for the user to do on the removal screen).
          needsPetRemovalStep: hasPetRemoval,
        });
        return newMap;
      });

      // Reset rollover pets flag
      setFilledFromRolloverPets(false);
    }

    if (hasPetRemoval) {
      goToSpecificStep(PET_DEMOS_STEPS.COMPLETE_PET_REMOVAL);
      return;
    }

    // Coming from pet removal screen, continue if there are ineligible pets
    if (hasIneligiblePets) {
      goToSpecificStep(PET_DEMOS_STEPS.INELIGIBLE_PETS);
      return;
    }

    // Only show Agreements on the last term if eConsent has not been submitted
    if (isLastCoveragePeriod && !isEconsentSubmitted && !isAgreementSubmitted) {
      goToSpecificStep(PET_DEMOS_STEPS.AGREEMENTS);
      return;
    }

    // goes back to initial step 2
    goToSpecificStep(PET_DEMOS_STEPS.YOUR_POLICY);
  }, [
    coveragePeriodSelected,
    goToSpecificStep,
    ineligiblePets,
    isAgreementSubmitted,
    isEconsentSubmitted,
    isLastCoveragePeriod,
    petQuantityToRemove,
  ]);

  // on continue step 6
  const onCompletePetRemovalContinue = useCallback(() => {
    const hasIneligiblePets = !!(coveragePeriodSelected?.maxAge
      && ineligiblePets && ineligiblePets.length);

    // Mark step 6 (COMPLETE_PET_REMOVAL) as completed for this period
    const groupId = coveragePeriodSelected?.groupId;
    if (groupId) {
      setCoveragePeriodPetsData((prev) => {
        const newMap = new Map(prev);
        const existingData = prev.get(groupId) || {};
        newMap.set(groupId, {
          ...existingData,
          hasCompletedPetRemoval: true,
        });
        return newMap;
      });
    }

    if (hasIneligiblePets) {
      goToSpecificStep(PET_DEMOS_STEPS.INELIGIBLE_PETS);
      return;
    }

    // Only show Agreements on the last term if eConsent has not been submitted
    if (isLastCoveragePeriod && !isEconsentSubmitted && !isAgreementSubmitted) {
      goToSpecificStep(PET_DEMOS_STEPS.AGREEMENTS);
      return;
    }

    // goes back to initial step 2
    goToSpecificStep(PET_DEMOS_STEPS.YOUR_POLICY);
  }, [
    coveragePeriodSelected,
    goToSpecificStep,
    ineligiblePets,
    isAgreementSubmitted,
    isEconsentSubmitted,
    isLastCoveragePeriod,
  ]);

  // on continue step 7 (ineligible pets)
  const onReviewIneligiblePetsContinue = useCallback(() => {
    // Mark step 7 (INELIGIBLE_PETS) as completed for this period
    const groupId = coveragePeriodSelected?.groupId;
    if (groupId) {
      setCoveragePeriodPetsData((prev) => {
        const newMap = new Map(prev);
        const existingData = prev.get(groupId) || {};
        newMap.set(groupId, {
          ...existingData,
          hasCompletedIneligiblePets: true,
        });
        return newMap;
      });
    }

    // Only show Agreements on the last term if eConsent has not been submitted
    if (isLastCoveragePeriod && !isEconsentSubmitted && !isAgreementSubmitted) {
      goToSpecificStep(PET_DEMOS_STEPS.AGREEMENTS);
      return;
    }

    // goes back to initial step 2
    goToSpecificStep(PET_DEMOS_STEPS.YOUR_POLICY);
  }, [
    coveragePeriodSelected,
    goToSpecificStep,
    isAgreementSubmitted,
    isEconsentSubmitted,
    isLastCoveragePeriod,
  ]);

  const onSubmitAgreement = useCallback((declarationsFromAgreement) => {
    if (declarationsFromAgreement) {
      setAgreementDeclarations({
        ...declarationsFromAgreement,
        isEconsentSubmitted: true,
      });
    }
    setIsAgreementSubmitted(true);
    goToSpecificStep(PET_DEMOS_STEPS.YOUR_POLICY);
  }, [goToSpecificStep]);

  const onSubmitSuccess = useCallback(() => {
    if (isWebView) {
      // temporarylly closes the webview
      sendPostMessage(POST_MESSAGE_TYPES.CLOSE_PAGE);
    } else {
      // in petcloud, we go to home
      history.push(HOME);
    }
  }, [history, isWebView, sendPostMessage]);

  const onSubmit = useCallback(() => {
    if (!petDemosData?.certificateNumber) {
      return;
    }

    const declarations = agreementDeclarations || {};

    dispatch(submitPetDemographics({
      coveragePeriodPetsData,
      declarations,
      policyDisplayNumber: petDemosData.certificateNumber,
    }));
  }, [
    agreementDeclarations,
    coveragePeriodPetsData,
    dispatch,
    petDemosData,
  ]);

  // Persist form state to localStorage so a page refresh doesn't lose data
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        agreementDeclarations,
        coveragePeriodPetsData: mapToEntries(coveragePeriodPetsData),
        coveragePeriodSelected,
        filledFromRolloverPets,
        isAgreementSubmitted,
        petDemographics,
        replacedSlotsOriginal: mapToEntries(replacedSlotsOriginal),
        selectedPetsForRemoval,
        selectedRolloverPetsByGroupId: mapToEntries(
          selectedRolloverPetsByGroupId,
        ),
        skippedRolloverGroupIds: setToArray(skippedRolloverGroupIds),
      }));
    } catch {
      // ignore storage errors (e.g. quota exceeded)
    }
  }, [
    agreementDeclarations,
    coveragePeriodPetsData,
    coveragePeriodSelected,
    filledFromRolloverPets,
    isAgreementSubmitted,
    petDemographics,
    replacedSlotsOriginal,
    selectedPetsForRemoval,
    selectedRolloverPetsByGroupId,
    skippedRolloverGroupIds,
    storageKey,
  ]);

  // Navigate to success/failure steps based on Redux state
  useEffect(() => {
    if (successSubmitting) {
      localStorage.removeItem(storageKey);
      goToSpecificStep(PET_DEMOS_STEPS.SUBMIT_SUCCESS);
    } else if (errorSubmitting) {
      goToSpecificStep(PET_DEMOS_STEPS.SUBMIT_FAILURE);
    }
  }, [errorSubmitting, goToSpecificStep, storageKey, successSubmitting]);

  // True while waiting for the post-submit pets refresh to resolve.
  const awaitingPetsRefreshRef = useRef(false);

  const onSubmitContinuePress = useCallback(() => {
    if (isWebView) {
      onSubmitSuccess();
      return;
    }

    // Wait for the pets refresh to resolve before going to Home.
    awaitingPetsRefreshRef.current = true;
    dispatch(refreshPetsAfterSubmit());
  }, [dispatch, isWebView, onSubmitSuccess]);

  useEffect(() => {
    if (awaitingPetsRefreshRef.current && !refreshingPets) {
      awaitingPetsRefreshRef.current = false;
      onSubmitSuccess();
    }
  }, [refreshingPets, onSubmitSuccess]);

  const onGoBackAndRetryPress = useCallback((
    onGoBackPress = () => { },
  ) => () => {
    // Reset submission state to allow retry
    dispatch(resetPetDemographicsSubmit());
    onGoBackPress();
  }, [dispatch]);

  return {
    adjustedPeriodCounts,
    adjustedRemovalDates,
    coveragePeriodPetsData,
    coveragePeriodSelected,
    declarations: petDemosData?.declarations || {},
    errorSubmitting,
    filledFromRolloverPets,
    getOriginalPetName,
    handleCancelReplacement,
    handleConfirmReplacement,
    handleReplacePetSave,
    handleSelectedPetsForRemovalChange,
    handleUndoReplace,
    handleUpdatePetDemographics,
    ineligiblePets,
    inWelcomeScreen,
    isPostSubmissionMode,
    isReplaced,
    isWebView,
    loading: !session.userInfo,
    maxAgeByPeriod,
    onCompletePetInfoContinuePress,
    onCompletePetRemovalContinue,
    onCoveragePeriodSelected,
    onGoBackAndRetryPress,
    onReviewIneligiblePetsContinue,
    onSubmit,
    onSubmitAgreement,
    onSubmitContinuePress,
    petDemographics,
    petDemosData,
    petQuantityToRemove,
    petRemovals,
    redirectionRoute,
    refreshingPets,
    selectedPetsForRemoval,
    setSelectedRolloverPetsForGroup,
    setSkippedRolloverGroupId,
    submitting,
    successSubmitting,
    usePendingDemographicsFF,
    userName,
  };
};

export { usePetDemographics };
